/** A borrower record as far as recipient resolution is concerned. */
export interface IBorrowerRecipientSource {
  email?: string | null
  additionalEmails?: string[] | null
}

/**
 * Split one stored value into addresses.
 *
 * A record migrated from a prior servicer can still carry several addresses in the single `email`
 * column, separated by whatever the export used. Anything that does not look like a list of
 * addresses is handed back whole, so a stray separator inside one address is left for a human to
 * correct rather than silently torn in two. Mirrors `splitEmailList` in the backend.
 */
export const splitEmailList = (value?: string | null): string[] => {
  const whole = `${value ?? ''}`.trim()
  if (!whole) return []
  const parts = whole
    .split(/[;,:\s]+/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0)
  if (parts.length < 2) return parts
  return parts.every((email) => email.includes('@')) ? parts : [whole]
}

/**
 * Every address a borrower should receive correspondence at: the primary first, then the
 * additional addresses, trimmed and de-duplicated case-insensitively.
 *
 * Correspondence only, and the counterpart of the backend's `borrowerRecipients`: identity uses
 * keep reading `borrower.email` on its own, because portal ownership and signing links match that
 * column exactly.
 */
export const borrowerRecipients = (borrower?: IBorrowerRecipientSource | null): string[] => {
  if (!borrower) return []
  const all = [...splitEmailList(borrower.email), ...(borrower.additionalEmails || []).flatMap(splitEmailList)]
  const seen = new Set<string>()
  return all.filter((email) => {
    const key = email.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** One row of a loan-parties email picker. */
export interface IBorrowerEmailParty {
  party: string
  email: string
}

/**
 * The picker rows for a borrower: one selectable row per address, all under the same party label.
 *
 * The label is shared deliberately. Admin > Email > Default recipients keys its To/Cc/Bcc defaults
 * and its hide flag on the party label, so sharing it means a tenant that pre-checks "Borrower"
 * pre-checks every address the borrower keeps, and a tenant that hides the borrower hides all of
 * them, with no new settings to fill in.
 */
export const borrowerEmailParties = (
  borrower?: IBorrowerRecipientSource | null,
  party = 'Borrower',
): IBorrowerEmailParty[] => borrowerRecipients(borrower).map((email) => ({ party, email }))

/**
 * A pre-check map marking each of `emails` as To (or Cc/Bcc), for a dialog that opens with the
 * borrower already addressed.
 *
 * Returned as its own map so a caller can spread it alongside the other parties it pre-checks,
 * rather than overwriting them.
 */
export const emailDirDefaults = (
  emails: string[],
  dir: 'to' | 'cc' | 'bcc' = 'to',
): Record<string, 'to' | 'cc' | 'bcc'> => {
  const defaults: Record<string, 'to' | 'cc' | 'bcc'> = {}
  for (const email of emails) {
    if (email) defaults[email] = dir
  }
  return defaults
}
